import React from "react"
import dayjs from "dayjs"
import { useSelector } from "react-redux"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"

interface TransportInvoicePrintModalProps {
  show: boolean
  onClose: () => void
  onPrint: () => void
  invoice: any
  studentDetails: {
    firstName?: string
    lastName?: string
    className?: string
    section?: string
    grNumber?: string
  }
  // The student's OWN campus (from their enrollment) — always prefer this
  // over the globally-selected header campus, which can easily be a
  // different one than the student actually belongs to.
  branch?: {
    name?: string
    schoolName?: string
    blackLogoUrl?: string | null
  } | null
  printRef: React.RefObject<HTMLDivElement>
  calculateFinalAmount: (record: any) => number
}

const TransportInvoicePrintModal: React.FC<TransportInvoicePrintModalProps> = ({
  show,
  onClose,
  onPrint,
  invoice,
  studentDetails,
  branch,
  printRef,
  calculateFinalAmount,
}) => {
  // Fallback only — the student's own branch (passed in explicitly) or the
  // invoice's own populated branch should always be available in practice.
  const currentBranch = useSelector(selectCurrentBranch)
  if (!show || !invoice) return null
  const printBranch = branch || invoice.branch || currentBranch

  const createdBy = invoice.createdBy
    ? `${invoice.createdBy?.firstName || ""} ${invoice.createdBy?.lastName || ""}`.trim()
    : ""

  return (
    <div className="modal fade show main">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Transport Fee Invoice</h5>
            <button
              type="button"
              className="btn-close custom-btn-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="modal-body">
            <div ref={printRef}>
              <div className="invoice-print printable-content printable-main">
                <div className="row mb-3">
                  <div className="col-6">
                    <p className="date">Date: {dayjs(invoice.createdAt).format("DD-MM-YYYY")}</p>
                  </div>
                  <div className="col-6 text-end">
                    <p className="date">Receipt No: {invoice.invoiceNumber}</p>
                  </div>
                </div>

                <div className="text-center mb-2">
                  {printBranch?.blackLogoUrl ? (
                    <img
                      src={printBranch.blackLogoUrl}
                      alt={campusBrandName(printBranch)}
                      style={{ width: "70px", height: "70px", objectFit: "contain" }}
                      className="mb-1"
                    />
                  ) : null}
                  <h4 className="text-uppercase fw-bold mb-1 heading">{campusBrandName(printBranch).toUpperCase()}</h4>
                  {campusBranchLabel(printBranch) ? (
                    <p className="branch-paragraph">{campusBranchLabel(printBranch).toUpperCase()}</p>
                  ) : null}
                </div>

                <div className="mb-3 text-center">
                  <p className="date">
                    {studentDetails.firstName?.toUpperCase()} {studentDetails.lastName?.toUpperCase()}
                    {studentDetails.grNumber ? ` (${studentDetails.grNumber})` : ""}
                  </p>
                  <p className="class">
                    Class {studentDetails.className} - {studentDetails.section}
                  </p>
                </div>

                <table className="table mb-2">
                  <thead className="table-main">
                    <tr>
                      <th className="table-item">Month</th>
                      <th className="table-item">Details</th>
                      <th className="table-item">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.invoiceItems?.map((item: any, index: number) => {
                      const monthDisplay =
                        item.month && item.month !== "other" ? dayjs(item.month).format("MMM YYYY") : "-"
                      const detailText =
                        item.description ||
                        `${item?.feeGroup?.name || item?.feeGroupId?.name || "Transport"} - Fee`

                      return (
                        <tr key={index}>
                          <td className="table-item">{monthDisplay}</td>
                          <td className="table-item">{detailText}</td>
                          <td className="table-item-right">{item.amount} /=</td>
                        </tr>
                      )
                    })}

                    {(invoice.discount?.profile > 0 || invoice.discount?.manual > 0) && (
                      <tr>
                        <td className="table-item"></td>
                        <td className="table-item">Discount:</td>
                        <td className="table-item-right">
                          {(invoice.discount?.profile || 0) + (invoice.discount?.manual || 0)} /=
                        </td>
                      </tr>
                    )}

                    {invoice.fine > 0 && (
                      <tr>
                        <td className="table-item"></td>
                        <td className="table-item">Fine:</td>
                        <td className="table-item-right">{invoice.fine} /=</td>
                      </tr>
                    )}

                    <tr>
                      <td colSpan={2} className="total-left">
                        Total:
                      </td>
                      <td className="total-right">{calculateFinalAmount(invoice)} /=</td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-3">
                  <p className="note">
                    <strong>Note:</strong> Fee once deposited is not refundable or adjustable.
                  </p>
                </div>

                {createdBy ? (
                  <div className="mt-2 text-center">
                    <p className="note">
                      <strong>Generated By: {createdBy}</strong>
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-light me-2" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={onPrint}>
              <i className="ti ti-printer me-2"></i>
              Print
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransportInvoicePrintModal
